import type { TourProgress } from "@/services/setup_progress";
import { RESUMABLE_TOUR_KEYS } from "@/tours/resumableTourKeys";
import type { TourDefinition } from "@/tours/types";
import { pageFocus } from "@/composables/guidedTour/bubble";
import {
  loadRegistryInTime,
  type Registry,
  registryIfLoaded,
} from "@/composables/guidedTour/loadRegistry";
import {
  activeTourKey,
  resetRun,
  session,
  startingTour,
} from "@/composables/guidedTour/state";

const RESUME_WINDOW_MS = 30 * 60 * 1000;
// Kept in this tab as soon as the step shows: its server save can still be
// queued when the page unloads for the sign-in.
const RESUME_MARKER_KEY = "fyr.tourResume";

interface ResumeMarker {
  tourKey: string;
  step: number;
  at: number;
}

export function writeResumeMarker(marker: ResumeMarker | null) {
  try {
    if (marker) {
      sessionStorage.setItem(RESUME_MARKER_KEY, JSON.stringify(marker));
    } else {
      sessionStorage.removeItem(RESUME_MARKER_KEY);
    }
  } catch {
    // Storage blocked: the server's copy is what's left.
  }
}

// Read once: a note left where it can't be resumed mustn't fire later.
// Undefined when storage is blocked.
function takeResumeMarker(): ResumeMarker | null | undefined {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(RESUME_MARKER_KEY);
    sessionStorage.removeItem(RESUME_MARKER_KEY);
  } catch {
    return undefined;
  }
  if (!raw) return null;
  try {
    const marker = JSON.parse(raw) as Partial<ResumeMarker>;
    return typeof marker.tourKey === "string" &&
      typeof marker.step === "number" &&
      typeof marker.at === "number"
      ? (marker as ResumeMarker)
      : null;
  } catch {
    return null;
  }
}

// Server rows tried once each when this tab can't keep its own note.
const resumeChecked = new Set<string>();

export function createResume(engine: {
  progressRows: () => TourProgress[] | undefined;
  isOnRoute: (route: string | string[]) => boolean;
  showStep: (tour: TourDefinition, index: number) => Promise<void>;
}) {
  function resumeInterrupted() {
    if (activeTourKey.value) return;
    const marker = takeResumeMarker();
    // Only this tab's own note: the server's rows are per user, so one could
    // be a tour left in another tab. They stand in only when storage is
    // blocked, each tried once.
    const candidates =
      marker !== undefined
        ? marker
          ? [marker]
          : []
        : (engine.progressRows() ?? [])
            .filter(
              (row) =>
                row.status === "in_progress" &&
                !resumeChecked.has(`${row.tourKey}:${row.updatedAt}`),
            )
            .map((row) => {
              resumeChecked.add(`${row.tourKey}:${row.updatedAt}`);
              return {
                tourKey: row.tourKey,
                step: row.step,
                at: Date.parse(row.updatedAt),
              };
            });
    const recent = candidates.filter(
      (row) =>
        RESUMABLE_TOUR_KEYS.has(row.tourKey) &&
        Date.now() - row.at <= RESUME_WINDOW_MS,
    );
    if (recent.length === 0) return;
    const registry = registryIfLoaded();
    if (registry) {
      resume(registry, recent);
      return;
    }
    const request = session.runRequest;
    startingTour.value = true;
    loadRegistryInTime().then(
      (loaded) => {
        if (request !== session.runRequest) return;
        startingTour.value = false;
        if (!activeTourKey.value) resume(loaded, recent);
      },
      () => {
        if (request === session.runRequest) startingTour.value = false;
      },
    );
  }

  function resume(
    loaded: Registry,
    candidates: { tourKey: string; step: number }[],
  ) {
    for (const row of candidates) {
      const tour = loaded.TOURS[row.tourKey];
      const step = tour?.steps[row.step];
      if (!step?.resumable) continue;
      const pageStep = tour.steps
        .slice(0, row.step + 1)
        .findLastIndex((s) => s.route);
      const page = tour.steps[pageStep]?.route;
      if (page && !engine.isOnRoute(page)) continue;
      activeTourKey.value = tour.key;
      resetRun({
        returnFocus: pageFocus(),
        resumed: true,
        // The same run as before the sign-in: its start was already counted,
        // and saved.
        trackedTour: tour,
        restarted: true,
      });
      // From the start of the step's page: someone who gave up on the
      // sign-in is back at "pick a platform", and for someone who finished
      // it the steps they already did move on by themselves.
      void engine.showStep(tour, Math.max(pageStep, 0));
      return;
    }
  }

  return { resumeInterrupted };
}
